/**
 *  @class
 *  @function HeaderV2
 *  Desktop dropdowns / mega menu, mobile drawer, sticky state and the
 *  --header-height / --announcement-height / --header-offset vars used across the theme.
 */
if (!customElements.get('header-v2')) {
  class HeaderV2 extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.details = Array.from(this.querySelectorAll('.hv2__details'));
      this.drawer = this.querySelector('.hv2-drawer');
      this.toggle = this.querySelector('.hv2__toggle');
      this.desktop = window.matchMedia('(min-width: 1068px)');
      this.hoverCapable = window.matchMedia('(hover: hover)');
      this.openOnHover = this.dataset.openOnHover === 'true';

      this.onKeyUp = this.onKeyUp.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.details.forEach((details) => this.setupDetails(details));

      this.toggle?.addEventListener('click', () => this.openDrawer());
      this.querySelectorAll('[data-hv2-drawer-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
      this.desktop.addEventListener('change', () => {
        this.closeAllDetails();
        this.closeDrawer(false);
      });

      document.addEventListener('keyup', this.onKeyUp);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.dataset.sticky === 'true') {
        document.body.classList.add('header-sticky--active');
      }
      this.onScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.body.classList.remove('overflow-hidden', 'header-sticky--active');
    }

    /* Desktop dropdowns */
    setupDetails(details) {
      const item = details.closest('.hv2__item');
      const summary = details.querySelector('summary');

      details.addEventListener('toggle', () => {
        if (details.open) {
          this.details.forEach((other) => other !== details && (other.open = false));
        }
        this.classList.toggle('has-open-menu', this.details.some((d) => d.open));
      });

      if (!this.openOnHover) return;

      item.addEventListener('mouseenter', () => {
        if (this.hoverMode()) details.open = true;
      });
      item.addEventListener('mouseleave', () => {
        if (this.hoverMode()) details.open = false;
      });
      // With hover-open, a click on an already open item keeps it open.
      summary.addEventListener('click', (event) => {
        if (this.hoverMode() && details.open && event.detail > 0) event.preventDefault();
      });
      // Close when keyboard focus leaves the item.
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) details.open = false;
      });
    }

    hoverMode() {
      return this.openOnHover && this.desktop.matches && this.hoverCapable.matches;
    }

    closeAllDetails() {
      this.details.forEach((details) => (details.open = false));
    }

    onDocumentClick(event) {
      if (!this.details.some((d) => d.open)) return;
      if (!event.target.closest('.hv2__item')) this.closeAllDetails();
    }

    onKeyUp(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      const open = this.details.find((d) => d.open);
      if (open) {
        open.open = false;
        open.querySelector('summary').focus();
      }
      if (this.drawer?.classList.contains('is-open')) this.closeDrawer();
    }

    /* Mobile drawer */
    openDrawer() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('inert');
      this.drawer.classList.add('is-open');
      this.section?.classList.add('hv2-drawer-open');
      this.toggle?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      this.drawer.querySelector('.hv2-drawer__close')?.focus({ preventScroll: true });
    }

    closeDrawer(returnFocus = true) {
      if (!this.drawer?.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('inert', '');
      this.toggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      setTimeout(() => this.section?.classList.remove('hv2-drawer-open'), 300);
      if (returnFocus) this.toggle?.focus({ preventScroll: true });
    }

    /* Sticky + layout vars */
    onScroll() {
      const root = document.documentElement.style;
      root.setProperty('--header-height', `${this.offsetHeight}px`);
      if (this.section) {
        root.setProperty('--header-offset', `${this.section.getBoundingClientRect().top}px`);
      }
      const announcement = document.querySelector('.announcement-bar-section');
      if (announcement) {
        root.setProperty('--announcement-height', `${announcement.clientHeight}px`);
      }
      if (this.dataset.sticky === 'true') {
        const offset = this.getBoundingClientRect().top + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY > 0 && window.scrollY >= offset);
      }
    }

    /* Theme editor: reveal the selected block */
    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      if (event.target.closest('.hv2-drawer')) {
        this.openDrawer();
        event.target.scrollIntoView({ block: 'nearest' });
        return;
      }
      const details = event.target.closest('.hv2__details');
      if (details) details.open = true;
    }

    onBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      const details = event.target.closest('.hv2__details');
      if (details) details.open = false;
    }
  }
  customElements.define('header-v2', HeaderV2);
}
